/**
 * Curriculum tree lazy-load schema.
 *
 * The learner-side "Create revision area" dialog walks the curriculum tree
 * on demand — fetch domains, click + to expand into subdomains, click + again
 * to drill in, etc. This avoids shipping the full tree (hundreds of thousands
 * of nodes at target depth) to the client.
 *
 * One request returns the children of a parent plus, for each child,
 * metadata the picker needs to decide whether it's worth showing / selectable:
 *
 *   - `hasChildren` — whether a subsequent expand would yield anything
 *   - `availableStepCount` — revisable steps in the learner's library under
 *     this subtree (0 means "you don't have content here — the picker will
 *     still show it but the Revise button will be disabled")
 */
import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
export const CurriculumTreeNodeSchema = z
    .object({
    id: z.string().uuid(),
    displayName: z.string().openapi({
        description: 'Human-readable node name.',
        example: 'Cardiology',
    }),
    slug: z.string().openapi({
        description: 'URL-safe identifier (unique within parent).',
        example: 'cardiology',
    }),
    depth: z.number().int().openapi({
        description: 'Raw curriculum depth. Current data convention: roots (domains) are at depth 1, L1 subcategories at depth 2, and so on. Clients checking "is this a root?" should prefer the structural signal — in the tree picker that is "node has no parent in the returned list" — rather than comparing to a literal depth value.',
        example: 1,
    }),
    hasChildren: z.boolean().openapi({
        description: 'True when this node has descendants to expand into.',
        example: true,
    }),
    availableStepCount: z.number().int().openapi({
        description: 'Revisable steps in the learner\'s currently-accessible library tagged to this node or any descendant. Zero means the learner has no content under this subtree; the picker should disable the Revise CTA.',
        example: 42,
    }),
})
    .openapi('CurriculumTreeNode');
export const CurriculumTreeChildrenQuerySchema = z
    .object({
    /**
     * Parent node to expand. When omitted, the roots of the tree (domain
     * level) are returned.
     */
    parentId: z.string().uuid().optional(),
})
    .openapi('CurriculumTreeChildrenQuery');
export const CurriculumTreeChildrenResponseSchema = z
    .object({
    nodes: z.array(CurriculumTreeNodeSchema),
})
    .openapi('CurriculumTreeChildrenResponse');
//# sourceMappingURL=curriculum-tree.schema.js.map